import React from 'react';
import { Space, Typography } from 'antd';
import {
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileWordOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

function firstTruthy(...values) {
  return values.find(Boolean);
}

function conditionalValue(condition, whenTrue, whenFalse) {
  return condition ? whenTrue : whenFalse;
}

function resolveAttachmentName(attachment) {
  const record = Object.assign({}, attachment);
  return String(firstTruthy(record.filename, record.name, '未命名附件')).trim() || '未命名附件';
}

function resolveAttachmentIcon(attachment) {
  const record = Object.assign({}, attachment);
  const name = resolveAttachmentName(attachment).toLowerCase();
  const mimeType = String(firstTruthy(record.mime_type, record.type) ?? '').toLowerCase();
  const iconRules = [
    { matches: [mimeType.startsWith('image/'), /\.(png|jpg|jpeg|gif|webp|bmp|svg)$/i.test(name)], icon: FileImageOutlined },
    { matches: [mimeType.includes('pdf'), name.endsWith('.pdf')], icon: FilePdfOutlined },
    {
      matches: [
        mimeType.includes('msword'),
        mimeType.includes('officedocument.wordprocessingml'),
        name.endsWith('.doc'),
        name.endsWith('.docx'),
      ],
      icon: FileWordOutlined,
    },
    { matches: [mimeType.startsWith('text/'), /\.(txt|md|rtf|csv)$/i.test(name)], icon: FileTextOutlined },
  ];
  return iconRules.find(({ matches }) => matches.some(Boolean))?.icon ?? FileOutlined;
}

function AttachmentPill(props) {
  const { attachment, index, isUser, themeTokens } = { isUser: false, ...props };
  const record = Object.assign({}, attachment);
  const Icon = resolveAttachmentIcon(attachment);
  const name = resolveAttachmentName(attachment);
  const key = `${firstTruthy(record.id, record.uid, record.source_id, name)}-${index}`;
  const textColor = conditionalValue(isUser, '#1f2d3d', themeTokens.textPrimary);
  const iconColor = conditionalValue(isUser, '#475569', themeTokens.textSecondary);
  const bgColor = conditionalValue(isUser, '#e8efff', 'rgba(15, 23, 42, 0.06)');
  const borderColor = conditionalValue(isUser, '#b7c9ff', themeTokens.surfaceBorder);

  return (
    <div
      key={key}
      style={{
        alignItems: 'center',
        background: bgColor,
        border: `1px solid ${borderColor}`,
        borderRadius: 999,
        display: 'inline-flex',
        gap: 6,
        maxWidth: 180,
        minWidth: 180,
        padding: '4px 10px',
      }}
      title={name}
    >
      <Icon style={{ color: iconColor, flex: '0 0 auto', fontSize: 14 }} />
      <Text
        style={{
          color: textColor,
          fontSize: 12,
          marginBottom: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {name}
      </Text>
    </div>
  );
}

export function AttachmentPills(props) {
  const { attachments, isUser, themeTokens } = { attachments: [], isUser: false, ...props };
  if (!Array.isArray(attachments) || attachments.length === 0) {
    return null;
  }
  return (
    <Space orientation="vertical" size={6}>
      {attachments.map((attachment, index) => (
        <AttachmentPill
          key={`${firstTruthy(
            Object.assign({}, attachment).id,
            Object.assign({}, attachment).uid,
            Object.assign({}, attachment).source_id,
            Object.assign({}, attachment).filename,
            Object.assign({}, attachment).name,
            'attachment'
          )}-${index}`}
          attachment={attachment}
          index={index}
          isUser={isUser}
          themeTokens={themeTokens}
        />
      ))}
    </Space>
  );
}
